import { createContext, use } from 'react'

/** The `action` slot every search screen renders in its toolbar for saved searches (SM-27). */
export const savedSearchesSlot = 'search.saved-searches'

/**
 * What a saved-search picker needs from the screen it sits on: the screen's key, its current
 * search as a URL query, and a way to open a saved one.
 */
export interface SearchScreenContextValue {
  /** The screen's key, such as `site-management.users`. */
  readonly screen: string
  /** The current search as URL parameters (`q`, `filter`, `sort`, `includeInactive`, `columns`, `tz`). */
  readonly query: string
  /** Opens a saved search, given as the `query` it was saved with. */
  readonly apply: (query: string) => void
  /**
   * Whether the screen opened with no search of its own and the viewer has not searched since.
   * Only then may a saved default replace the search.
   */
  readonly isUnsearched?: boolean
  /** The API query the shown results loaded with, relative periods resolved; `null` until they load. */
  readonly loadedQuery?: string | null
}

export const SearchScreenContext = createContext<SearchScreenContextValue | null>(null)

/** The search screen around a saved-search picker or a header action. Only inside the `search.saved-searches` slot or the header. */
export function useSearchScreen(): SearchScreenContextValue {
  const value = use(SearchScreenContext)
  if (value === null) {
    throw new Error('useSearchScreen is only available inside a search screen.')
  }
  return value
}
